<template>
    <Popper class="hrm_task_assignee" :disabled="disabled" :placement="placement" @open:popper="popperOpened">
        <button class="w-full hrm_option_selection_box">
            <template v-if="showCountWise">
                <template v-if="visibleTags && visibleTags.length > 0">
                    <span class="relative flex items-center gap-2 w-full flex-wrap
                        h-full  hrm_task_tags_selector px-4 justify-start">
                        <span v-for="tag in visibleTags" :key="tag.id" class="hrm-task-tag-option flex items-center 
                gap-1 px-1 py-1 rounded-sm" :style="{ backgroundColor: tag?.color }">
                            <span class="hrm-task-tag-block flex items-center gap-1">
                                <span class="hrm-task-tag-option-text font-bold text-gray-100 text-xs">
                                    {{ tag?.[label] }}
                                </span>
                                <span class="hrm-task-tag-delete text-gray-100" v-if="canClear">
                                    <Icon class="flex-none" icon="mingcute:close-fill" width="8px" height="8px"
                                        @click.stop="removeTag(tag.id)" />
                                </span>
                            </span>
                        </span>
                        <span v-if="selectedItemsTags.length > 2" class="hrm-task-tag-more">
                            +{{ selectedItemsTags.length - 2 }}
                        </span>
                    </span>
                </template>
            </template>
            <template v-else>
                <template v-if="selectedItemsTags && selectedItemsTags.length > 0">
                    <slot name="selectedItem" :items="selectedItemsTags" :removeTag="removeTag">
                        <span class="relative flex items-center gap-2 w-full h-full 
                         hrm_task_tags_selector px-4 justify-start flex-wrap">
                            <span v-for="tag in selectedItemsTags" :key="tag.id" class="hrm-task-tag-option flex items-center 
                gap-1 px-1 py-1 rounded-sm" :style="{ backgroundColor: tag?.color }">
                                <span class="flex items-center gap-1">
                                    <span class="hrm-task-tag-option-text font-bold text-xs text-gray-100">
                                        {{ tag?.[label] }}
                                    </span>
                                    <span class="hrm-task-tag-delete text-gray-100" v-if="canClear">
                                        <Icon class="flex-none" icon="mingcute:close-fill" width="8px" height="8px"
                                            @click.stop="removeTag(tag.id)" />
                                    </span>
                                </span>
                            </span>
                        </span>
                    </slot>
                </template>
            </template>
            <slot v-if="selectedItemsTags && selectedItemsTags.length == 0" name="noItem"
                :text="noItem || 'Select item'">
                <span class="w-full flex items-center gap-2 text-start hrm_no_option">
                    {{ noItem || "Select item" }}
                </span>
            </slot>
        </button>
        <template #content>
            <div class="">
                <div class="flex items-center hrm_option_search">
                    <Icon v-if="isShowSearchIcon" class="flex-none" icon="ic:outline-search" width="18px"
                        height="18px" />
                    <input ref="searchInputRef" type="text" v-model="searchItem" @change.stop
                        :placeholder="searchInputPlaceholder || 'Search...'" @keypress.enter="onCreateTags" />
                    <button v-if="searchItem" @click="searchItem = ''" class="px-1 py-1">
                        <Icon icon="mingcute:close-fill" width="1.2em" height="1.2em" style="color: white" />
                    </button>
                </div>
                <div class="hrm_options_list">
                    <ul v-if="filteredOptions && filteredOptions.length > 0">
                        <template v-for="option in filteredOptions" :key="option">
                            <li class="cursor-pointer hrm_option_item"
                                @click="selectedTagsIds && selectedTagsIds.includes(option?.[valueProp]) ? removeTag(option?.[valueProp]) : setOptionValue(option)">
                                <div class="flex items-center gap-2">
                                    <slot name="option" :option="option" :selectedTagsIds="selectedTagsIds">
                                        <div class="w-full flex justify-between">
                                            <span>
                                                {{ option?.[label] }}
                                            </span>
                                            <span
                                                v-if="selectedTagsIds && selectedTagsIds.includes(option?.[valueProp])">
                                                <Icon icon="mingcute:check-fill" width="1.2em" height="1.2em"
                                                    style="color: #41ff41" />
                                            </span>
                                        </div>
                                    </slot>
                                </div>
                            </li>
                        </template>
                    </ul>
                    <div class="flex items-center justify-center hrm_no_options" v-else>
                        <p v-html="getNoResultFound" class="flex flex-col justify-center items-center py-6"></p>
                    </div>
                </div>
            </div>
        </template>
    </Popper>
</template>
<script setup>
import { ref, computed, inject, onMounted, nextTick } from "vue";
import Popper from "vue3-popper";
import { Icon } from "@iconify/vue";
import { setFontColor } from "../../../../Modules/ProjectManagement/Resources/js/helper/index";

const emits = defineEmits(["update:modelValue", "select", "clear"]);
const props = defineProps([
    "options",
    "label",
    "valueProp",
    "modelValue",
    "disabled",
    "canClear",
    "noItem",
    "noResultFound",
    "searchInputPlaceholder",
    "showCountWise",
    "placement",
    "isShowSearchIcon",
    "isCreateTags",
    "apiLink"
]);
const searchItem = ref();
const selectedValue = ref(null);
const selectedItemsTags = ref([]);
const selectedTagsIds = ref([])
const searchInputRef = ref(null);
const isSubmitting = ref(false);
const newTagForm = ref({});

const visibleTags = computed(() => {
    return selectedItemsTags.value && selectedItemsTags.value.length > 0
        ? selectedItemsTags.value.slice(0, 2)
        : [];
});

// Inject function to get task tags from parent component
const getTaskTags = inject("getTaskTags");
const taskInfo = inject("taskInfo");

//Task assigned data and methods
const filteredOptions = computed(() => {
    if (!searchItem.value) return props.options;
    return props.options.filter((option) =>
        option?.[props.label]
            .toLowerCase()
            .includes(searchItem.value.toLowerCase())
    );
});

const optionLabel = computed(() => {
    if (!props.options || props.modelValue === undefined) {
        return null;
    }

    return props.options.find(
        (option) => option?.[props.valueProp] === props.modelValue
    );
});

const getNoResultFound = computed(() => {
    if (props.isCreateTags && searchItem.value) {
        const noResultHtml = `<p>No results found</p>
            <p class="text-xs text-gray-500">Press Enter to create a new tag</p>`;
        return noResultHtml;
    }
    return props.noResultFound || "No results found";
});

// Set or clear task data
const setOptionValue = (option) => {
    selectedItemsTags.value.push(option);
    selectedTagsIds.value.push(option?.[props.valueProp]);
    emits("update:modelValue", selectedTagsIds.value);
    emits("change");
};

const clearValues = () => {
    selectedTagsIds.value = [];
    selectedItemsTags.value = [];
    emits("update:modelValue", selectedTagsIds.value);
    emits("clear");
};

const removeTag = (tagId) => {
    let removeTagId = selectedTagsIds.value.indexOf(tagId);
    selectedTagsIds.value.splice(removeTagId, 1);
    selectedItemsTags.value.splice(removeTagId, 1);
    emits("update:modelValue", selectedTagsIds.value);
    emits("change");
}

const getOption = () => {
    if (props.modelValue === undefined) {
        return null;
    }
    let taskTagsId = typeof props.modelValue === 'string' ? props.modelValue.split(',') : props.modelValue;

    if (taskTagsId && taskTagsId.length > 0) {
        taskTagsId.forEach((tagId) => {
            let option = props.options.find(
                (option) => option?.[props.valueProp] == tagId
            );
            if (option) {
                selectedItemsTags.value.push(option);
                selectedTagsIds.value.push(option?.[props.valueProp]);
            }
        });
    }
};

const onCreateTags = () => {
    if (props.isCreateTags && searchItem.value) {
        if (isSubmitting.value) return;

        isSubmitting.value = true;

        let formData = {
            name: searchItem.value,
            ...(newTagForm.value || {}),
        };
        submitForm(formData);
    }
}

const submitForm = async (formData) => {
    try {
        const res = await axios.post(route(props.apiLink), formData);

        if (res.status === 200) {
            getTaskTags();
        }
    } catch (error) {
        console.error(error);
    } finally {
        searchItem.value = '';
        isSubmitting.value = false;
    }
}


const getTextColor = (color) => {
    let textColor = setFontColor(color);
    return textColor;
}

const popperOpened = () => {
    nextTick(() => {
        if (searchInputRef.value) {
            searchInputRef.value.focus();
        }
    })
}

onMounted(() => {
    getOption();
    if (taskInfo && taskInfo.value) {
        newTagForm.value.project_id = taskInfo.value.project_id;
    }
});
</script>
<style scoped>
.hrm_options_list {
    padding: 4px 6px;
    max-height: 160px;
    overflow-y: auto;
}

.hrm_option_item {
    color: #8893a3;
    font-size: 14px;
    padding: 2px 4px;
    border-radius: 6px;
}

.hrm_single_label {
    color: #8893a3;
    font-size: 12px;
}

.dark .hrm_options_list .hrm_option_item:hover {
    background: #3d454f;
}

.hrm_options_list ul {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.hrm_option_selection_box {
    background: #35373b !important;
    border-radius: 6px;
}

.hrm_option_selection_box::placeholder {
    color: #a3a3a3 !important;
}

.hrm_no_option {
    font-size: 14px;
    padding: 6px 10px;
    color: #b2b9c3;
}

.hrm_option_search {
    padding: 8px 6px;
}

.dark .hrm_option_search {
    border-bottom: 1px solid #48505a;
    color: #c8ccd1;
}

.hrm_option_search input {
    width: 100%;
    padding: 6px 10px;
    font-size: 14px;
    line-height: 1;
    background: #4b5461;
    border-radius: 6px;
    margin: 0 6px;
    color: #c8ccd1;
}

.hrm_option_search input::placeholder {
    color: #c9ced4 !important;
}

.hrm_no_options {
    color: #9da5af;
    font-size: 13px;
    font-weight: 500;
}

.hrm_task_assignee .hrm_assigned_clear .cross_icon {
    display: flex !important;
    justify-content: end !important;
    align-items: end;
}

.hrm-task-tag-delete {
    display: none;
}

.hrm-task-tag-option:hover .hrm-task-tag-delete {
    display: flex;
    justify-content: center;
    align-items: center;
}

.hrm-task-tag-option {
    /* width: 75px; */
    max-width: 50px;
}

.hrm-task-tag-option:hover {
    max-width: 100%;
}

.hrm-task-tag-option:hover .hrm-task-tag-delete svg {
    width: 12px;
    height: 12px;
}

.hrm-task-tag-option .hrm-task-tag-option-text {
    /* min-width: 30px; */
    text-align: start;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    font-size: 10px !important;
    line-height: 1;
}

.hrm-task-tag-more {
    width: 20px;
    height: 20px;
    background-color: #353535;
    border-radius: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-weight: 500;
}

.hrm_task_tags_selector {
    min-height: 30px;
}
</style>
